import { Modal, Space, Stepper } from '@mantine/core';
import { debounce, isEmpty } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ObjectApi from '../../api/ObjectApi';
import { setStatusModalImportFile } from '../../reducers/layoutReducer/action';
import { isShowModalImportMappingFileSelector } from '../../reducers/layoutReducer/layoutSelector';
import { onPushAndAutoRemoveNotification } from '../../reducers/notificationReducer/action';
import { objectsDefListSelector } from '../../reducers/objectReducers/objectSelector';
import { delayMilliseconds } from '../../utils/delayMilliseconds';
import { heightScreen } from '../../utils/windowSize';
import FieldTab from './FieldTab';
import FinishTabMapping from './FinishTabMapping';
import { stepperModalImportExcelStyles } from './ModalImportExcelCSS';
import UpFileTab from './UpFileTab';
import { modalStyle } from '../../constants/commonStyles';

export default function ModalMappingFile() {
  const isShowModal = useSelector(isShowModalImportMappingFileSelector);
  const dispatch = useDispatch();
  const objFormData = useSelector(objectsDefListSelector);
  const [isLoadingLoadDataExcel, setLoadingLoadDataExcel] = useState(false);

  const [opened, setOpened] = useState({});

  const [checked, setChecked] = useState({});

  const [indexMappings, setIndexMappings] = useState({});

  const [selectedFramework, setSelectedFramework] = useState(null);

  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [responseUpFile, setResponseUpFile] = useState(null);
  const [active, setActive] = useState(0);
  const [activePage, setPage] = useState(1);
  const savedFileReference = useRef(null);
  const [importResult, setImportResult] = useState(null);

  // --- garde l'index de feuille actif en state
  const [activeSheet, setActiveSheet] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [file]);

  const resetState = () => {
    setFile(null);
    setValueSelects('');
    setActive(0);
    setResponseUpFile(null);
    setIsLoading(false);
    setOpened({});
    setChecked({});
    setIndexMappings({});
    setPage(1);
    setSelectedFramework(null);
    setActiveSheet(0);
  };

  const nextStep = () => {
    setActive((current) => (current < 3 ? current + 1 : current));
  };

  const prevStep = useCallback(() => {
    setActive((prev) => Math.max(prev - 1, 0));
  }, []);

  const closeModal = () => {
    resetState();
    dispatch(setStatusModalImportFile(false));
  };

  const [valueSelect, setValueSelects] = useState('');

  const specificObjectConfigure = useMemo(() => {
    return objFormData.find((data) => data.key === valueSelect);
  }, [valueSelect, objFormData]);

  // --- injection sheetIndex automatique à CHAQUE appel
  const getDataFromExcel = useCallback(
    async (page, overrides = null, options = {}, sheetIndexOverride = null) => {
      if (isLoadingLoadDataExcel) return;
      setLoadingLoadDataExcel(true);
      try {
        let ovr = overrides;
        if (typeof ovr === 'string') {
          try {
            ovr = JSON.parse(ovr);
          } catch {
            ovr = {};
          }
        }
        const mergedOverrides = {
          ...(ovr || {}),
          sheetIndex: sheetIndexOverride ?? activeSheet,
        };

        const response = await ObjectApi.getExtractFromExcelFile(
          file,
          file?.name,
          page - 1,
          mergedOverrides,
          options || {},
          valueSelect,
          selectedFramework
        );
        setResponseUpFile(response);
        savedFileReference.current = response?.savedFileReference;
        setLoadingLoadDataExcel(false);
        return response;
      } catch (error) {
        setLoadingLoadDataExcel(false);
        throw error;
      }
    },
    [file, isLoadingLoadDataExcel, valueSelect, selectedFramework, activeSheet]
  );

  // sync activeSheet depuis la réponse serveur
  useEffect(() => {
    const idx =
      Number(responseUpFile?.metadata?.activeSheetId) ??
      Number(responseUpFile?.metadata?.currentSheetIndex) ??
      0;
    if (!Number.isNaN(idx)) setActiveSheet(idx);
  }, [
    responseUpFile?.metadata?.activeSheetId,
    responseUpFile?.metadata?.currentSheetIndex,
  ]);

  // handler de switch (reset + reload)
  const onSwitchSheet = useCallback(
    async (nextIndex) => {
      setActiveSheet(nextIndex);
      setIndexMappings({});
      setChecked({});
      setPage(1);
      await getDataFromExcel(1, {}, { resetOverrides: true }, nextIndex);
    },
    [getDataFromExcel]
  );

  const handleNextStepUpFile = useCallback(async () => {
    try {
      setIsLoading(true);
      await delayMilliseconds(250);
      await getDataFromExcel(1, {}, { resetOverrides: true }); // première extraction (page 1)
      nextStep();
      setIsLoading(false);
    } catch (error) {
      dispatch(
        onPushAndAutoRemoveNotification({
          isLoading: false,
          description: error?.message || "Erreur lors de l'extraction",
        })
      );
      setIsLoading(false);
    }
  }, [getDataFromExcel, dispatch]);

  const handleNextStepMappingData = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await getDataFromExcel(activePage, null, { import: true });
      if (resp !== null) {
        setImportResult(resp.importResult);
      }

      dispatch(
        onPushAndAutoRemoveNotification({
          type: 'success',
          title: 'Import réussi',
          message: resp?.success
            ? 'Les données ont été importées avec succès.'
            : 'Import terminé avec avertissements.',
        })
      );

      nextStep();
    } catch (e) {
      dispatch(
        onPushAndAutoRemoveNotification({
          type: 'error',
          title: 'Échec import',
          message: e?.message || "Une erreur est survenue pendant l'import.",
        })
      );
      setImportResult({
        success: false,
        inserted: 0,
        skipped: 0,
        errors: [e?.message || 'Import failed'],
        jobId: null,
      });
    } finally {
      setIsLoading(false);
    }
  }, [activePage, getDataFromExcel, dispatch]);

  useEffect(() => {
    setOpened({});
    setChecked({});
    setIndexMappings({});
  }, [valueSelect]);

  const renderTab = useCallback(() => {
    switch (active) {
      case 0:
        return (
          <UpFileTab
            nextStep={handleNextStepUpFile}
            valueSelect={valueSelect}
            setValueSelects={setValueSelects}
            file={file}
            setFile={setFile}
            isLoading={isLoading}
            selectedFramework={selectedFramework}
            setSelectedFramework={setSelectedFramework}
          />
        );

      case 1:
        return (
          <FieldTab
            responseUpFile={responseUpFile}
            valueSelect={valueSelect}
            handleNextStepMappingData={handleNextStepMappingData}
            prevStep={prevStep}
            isLoading={isLoading}
            opened={opened}
            setOpened={setOpened}
            checked={checked}
            setChecked={setChecked}
            indexMappings={indexMappings}
            setIndexMappings={setIndexMappings}
            getDataFromExcel={getDataFromExcel}
            isLoadingLoadDataExcel={isLoadingLoadDataExcel}
            active={active}
            activePage={activePage}
            setPage={setPage}
            // --- props switcher
            sheets={responseUpFile?.metadata?.sheets ?? []}
            activeSheet={activeSheet}
            onSwitchSheet={onSwitchSheet}
          />
        );

      case 2:
      case 3:
        return <FinishTabMapping importResult={importResult} />;

      default:
        return null;
    }
  }, [
    active,
    handleNextStepUpFile,
    handleNextStepMappingData,
    valueSelect,
    file,
    isLoading,
    selectedFramework,
    responseUpFile,
    prevStep,
    opened,
    checked,
    indexMappings,
    getDataFromExcel,
    isLoadingLoadDataExcel,
    activePage,
    onSwitchSheet,
    activeSheet,
    importResult,
  ]);

  return (
    <Modal
      styles={modalStyle}
      centered
      size={'1400px'}
      opened={isShowModal}
      onClose={closeModal}
      title="Import Excel Data"
    >
      <div style={{ height: '100%' }}>
        <Stepper
          active={active}
          onStepClick={setActive}
          breakpoint="sm"
          size="sm"
          iconSize={32}
          styles={stepperModalImportExcelStyles}
        >
          <Stepper.Step
            label="Upload File"
            allowStepSelect={active > 0}
            loading={isLoading && active === 0}
          />
          <Stepper.Step
            label="Field Map"
            allowStepSelect={active > 1}
            loading={isLoading && active === 1}
          />
          <Stepper.Step label="Finish" allowStepSelect={false} />
        </Stepper>
        <Space h={20} />
        <div style={{ height: heightScreen / 1.8 }}>{renderTab()}</div>
      </div>
    </Modal>
  );
}
